/**
 * Flagged item modal body when partIndex is null (e.g. vendor name mismatch).
 */
import React from 'react';
import { Box, Text, VStack } from '@chakra-ui/react';

export interface VendorMismatchContentProps {
    issue: any;
    getIssueTooltipText: (issue: any, hasLocalMatch?: boolean, asString?: boolean) => any;
    selectedVendor: string | null;
    onSelectVendor: (vendorName: string) => void;
    readOnly?: boolean;
    showSubmittedCopy?: boolean;
}

export function VendorMismatchContent({
    issue,
    getIssueTooltipText,
    selectedVendor,
    onSelectVendor,
    readOnly = false,
    showSubmittedCopy = false,
}: VendorMismatchContentProps) {
    const isVendor = issue?.issueType === 'vendor_mismatch';
    const hasMismatch = issue?.hasMismatch !== false;

    const nexVendors: string[] = Array.from(
        new Set(
            [
                ...(Array.isArray(issue?.nexVendorNames) ? issue.nexVendorNames : []),
                ...(issue?.nexVendorName ? [issue.nexVendorName] : []),
            ]
                .map((name) => String(name).trim())
                .filter((name) => name.length > 0)
        )
    );
    const reason: string | undefined = issue?.reason;

    return (
        <VStack spacing={4} align="stretch">
            <Box bg={hasMismatch ? 'orange.50' : 'blue.50'} border="1px solid" borderColor={hasMismatch ? 'orange.200' : 'blue.200'} borderRadius="md" p={3}>
                <Text fontSize="sm" color={hasMismatch ? 'orange.700' : 'blue.700'} fontWeight="medium">
                    {isVendor ? (
                        !hasMismatch ? (
                            showSubmittedCopy
                                ? 'See the options from NEX below:'
                                : 'Choose the vendor to use for matching and reconciliation.'
                        ) : reason === 'multiple_matches' ? (
                            `Multiple possible vendor matches for "${issue.invoiceVendorName || '(empty)'}".`
                        ) : (() => {
                            const inv = issue.invoiceVendorName || '(empty)';
                            const single = issue.nexVendorName != null ? String(issue.nexVendorName).trim() : '';
                            if (single !== '') return `Vendor name mismatch: "${inv}" vs NEX "${single}"`;
                            if (nexVendors.length > 0) {
                                return `Vendor name mismatch: "${inv}". NEX vendor options: ${nexVendors.join(', ')}`;
                            }
                            return `Vendor name mismatch: "${inv}". No vendor names on this NEX purchase order.`;
                        })()
                    ) : (
                        (issue ? getIssueTooltipText(issue) : 'No additional details available.')
                    )}
                </Text>
            </Box>

            {isVendor && nexVendors.length > 0 && (
                <>
                    <VStack align="stretch" spacing={3} maxH="300px" overflowY="auto" pr={1}>
                        {nexVendors.map((v) => {
                            const isSelected = selectedVendor === v;
                            return (
                                <Box
                                    key={v}
                                    as="button"
                                    onClick={() => {
                                        if (readOnly) return;
                                        onSelectVendor(v);
                                    }}
                                    textAlign="left"
                                    flexShrink={0}
                                    p={4}
                                    borderRadius="lg"
                                    border="2px solid"
                                    borderColor={isSelected ? 'blue.500' : 'gray.200'}
                                    bg={isSelected ? 'blue.50' : 'white'}
                                    boxShadow="none"
                                    cursor={readOnly ? 'default' : 'pointer'}
                                    transition="all 0.15s"
                                    _hover={readOnly ? {} : {
                                        borderColor: isSelected ? 'blue.500' : 'gray.300',
                                        boxShadow: 'none',
                                    }}
                                >
                                    <Text fontSize="sm" fontWeight="semibold" color={isSelected ? 'blue.700' : 'gray.800'}>
                                        {v}
                                    </Text>
                                </Box>
                            );
                        })}
                    </VStack>
                </>
            )}

            {isVendor && nexVendors.length === 0 && (
                <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
                    <Text fontSize="sm" color="gray.700">
                        No vendor options were returned from NEX for this RO.
                    </Text>
                </Box>
            )}
        </VStack>
    );
}
